import React, { useState } from "react";
import { Button, FormLabel } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import Placeholder from "components/Placeholder";
import {
  Container,
  ControlHeader,
  ControlContainer,
  OptionCards,
  QuestionsCardWrapper,
  Row,
} from "./styles";
import withFieldControl from "components/FormControl/FieldControl";
import { reorder } from "utils/calculation";
import TriviaStore, { Question } from "../../TriviaStore";
import QuestionCard from "./QuestionCard";
import Popup from "components/Popup";
import QuestionForm from "./QuestionForm";
import OptionForm from "./OptionForm";
import { SNACKBAR_TYPES } from "components/Snackbar/Snackbar";

type Props = {
  triviaId: number;
  isEditMode: boolean;
  questions: Question[];
};

type QuestionControlProps = Props & SnackbarContextProps & {};

const QuestionControl = ({
  questions = [],
  triviaId,
  isEditMode,
  setMessage,
}: QuestionControlProps) => {
  const [isAddQuestionOpen, setIsAddQuestionOpen] = useState(false);
  const [isAddOptionOpen, setIsAddOptionOpen] = useState(false);
  const [question, setQuestion] = useState<Question | null>(null);
  const [isQuestionEditMode, setIsQuestionEditMode] = useState(false);

  const toggleQuestionPopup = () => setIsAddQuestionOpen(val => !val);
  const toggleOptionPopup = () => setIsAddOptionOpen(val => !val);

  const [questionId, setQuestionId] = useState<number | null>(null);

  const onDragEnd = async (result: DropResult) => {
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    const reorderedQuestions: Question[] = reorder(
      questions,
      startIndex,
      endIndex,
    );

    const newOrder = reorderedQuestions.map(question => ({
      id: question.id,
    }));

    const payload = {
      newOrder,
      id: triviaId,
    };

    await TriviaStore.updateQuestionOrder(payload);
    await TriviaStore.fetchTriviaDetails(triviaId);
  };

  const onRemoveItem = async (question: Question) => {
    const payload = {
      id: triviaId,
      questionId: question.id,
    };
    await TriviaStore.deleteQuestion(payload);
    await TriviaStore.fetchTriviaDetails(triviaId);
  };

  const onEditQuestionClick = async (question: Question) => {
    toggleQuestionPopup();
    setQuestion(question);
    setIsQuestionEditMode(true);
  };

  const handleAddSubmit = async (values: Question) => {
    try {
      const data = {
        title: values.title.trim(),
        code: values.code.trim(),
        complement: values.complement.trim(),
        responseType: "Unique",
        isRequired: values.isRequired === "true",
        triviaID: +triviaId,
        displayOrder: 0,
      };
      if (isQuestionEditMode) {
        await TriviaStore.editTriviaQuestion({ id: values.id, ...data });
      } else {
        await TriviaStore.addQuestionToTrivia(data);
      }
      await TriviaStore.fetchTriviaDetails(triviaId);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    } finally {
      toggleQuestionPopup();
      setQuestion(null);
      setIsQuestionEditMode(false);
    }
  };

  return (
    <>
      <Container>
        <Row>
          <ControlHeader>
            <FormLabel>Questions</FormLabel>
            {isEditMode && (
              <ControlContainer>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={toggleQuestionPopup}
                >
                  Add Question
                </Button>
              </ControlContainer>
            )}
          </ControlHeader>
        </Row>

        <QuestionList
          questions={questions}
          isEditMode={isEditMode}
          onRemoveItem={onRemoveItem}
          onDragEnd={onDragEnd}
          onEditQuestionClick={onEditQuestionClick}
          onAddOptionClick={question => {
            toggleOptionPopup();
            setQuestionId(question.id);
          }}
        />
      </Container>

      {isAddQuestionOpen && (
        <Popup
          onClose={() => {}}
          canCloseByOuterClick={false}
          withoutExitButton
        >
          <QuestionForm
            isEditMode={isQuestionEditMode}
            question={question}
            onClose={() => {
              setQuestion(null);
              setIsQuestionEditMode(false);
              toggleQuestionPopup();
            }}
            onSubmit={handleAddSubmit}
            isLoading={TriviaStore.isAddQuestionLoading}
          />
        </Popup>
      )}
      {isAddOptionOpen && (
        <Popup
          onClose={toggleOptionPopup}
          canCloseByOuterClick={false}
          withoutExitButton
        >
          <OptionForm
            formTitle="Add Option"
            initialValues={{}}
            isEditMode={isEditMode}
            formData={{}}
            onClose={toggleOptionPopup}
            onSubmit={() => {}}
            questionId={questionId}
          />
        </Popup>
      )}
    </>
  );
};

export default withGlobalSnackbar(withFieldControl(QuestionControl));

const QuestionList = ({
  questions,
  isEditMode,
  onRemoveItem,
  onDragEnd,
  onAddOptionClick,
  onEditQuestionClick,
}: {
  questions: Question[];
  isEditMode: boolean;
  onRemoveItem: (q: Question) => void;
  onDragEnd: (result: DropResult) => void;
  onAddOptionClick: (q: Question) => void;
  onEditQuestionClick: any;
}) => {
  if (!questions.length) {
    return (
      <Placeholder height={132} width={132} text={"No questions added yet."} />
    );
  }

  if (!isEditMode) {
    return (
      <>
        <QuestionsCardWrapper>
          {questions?.map((question: any) => (
            <QuestionCard
              onEditQuestionClick={onEditQuestionClick}
              key={question.id}
              title={question.title}
            />
          ))}
        </QuestionsCardWrapper>
      </>
    );
  }

  return (
    <Row>
      <OptionCards>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {questions.map((item: Question, index: number) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <QuestionCard
                        key={item.id}
                        title={item.title}
                        onRemove={() => onRemoveItem(item)}
                        onEditQuestionClick={() => onEditQuestionClick(item)}
                        dragHandleProps={provided.dragHandleProps}
                        innerRef={provided.innerRef}
                        isDragging={snapshot.isDragging}
                        {...provided.draggableProps}
                        isEditMode
                        onAddOptionClick={() => onAddOptionClick(item)}
                      />
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </OptionCards>
    </Row>
  );
};
